import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as pdfjsLib from "pdfjs-dist";
import { api } from "@/lib/api";
import { generateEndorsementPdf } from "@/lib/endorsement";
import type { EndorsementInput } from "@/types";
import { getStoredToken, isTokenExpired } from "@/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.mjs`;

interface ShareComment {
  id: number;
  commenter_name: string;
  body: string;
  created_at: number;
  page_number: number;
  parent_id: number | null;
}

function formatTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const AVATAR_COLORS = [
  "bg-blue-500", "bg-violet-500", "bg-rose-500",
  "bg-amber-500", "bg-emerald-500", "bg-cyan-500",
];

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  const hash = name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function Avatar({ name, sm }: { name: string; sm?: boolean }) {
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${getAvatarColor(name)} ${sm ? "h-7 w-7 text-[10px]" : "h-8 w-8 text-[11px]"}`}
    >
      {getInitials(name)}
    </div>
  );
}

export function SharePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("id") ?? "";
  const qc = useQueryClient();

  const [candidateName, setCandidateName] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [body, setBody] = useState("");
  const [copied, setCopied] = useState(false);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyBody, setReplyBody] = useState("");

  useEffect(() => {
    const token = getStoredToken();
    if (!token || isTokenExpired(token)) {
      const redirect = `/share/pdf${id ? `?id=${id}` : ""}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
    }
  }, [id, navigate]);

  const isAuthed = (() => {
    const token = getStoredToken();
    return !!token && !isTokenExpired(token);
  })();

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.me(),
    enabled: isAuthed,
  });

  const commenterName = meData?.user
    ? (meData.user.display_name || meData.user.username)
    : "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["share", id],
    queryFn: () => api.getShare(id),
    enabled: !!id && isAuthed,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (!data?.endorsement_input) return;
    setPdfError(null);
    try {
      const input: EndorsementInput = JSON.parse(data.endorsement_input);
      setCandidateName(input.candidateName ?? null);
      document.title = input.candidateName
        ? `${input.candidateName} Endorsement Form`
        : "Endorsement Form";
      const doc = generateEndorsementPdf(input);
      setPdfBlob(doc.output("blob"));
      setCurrentPage(1);
    } catch {
      setPdfError("Could not render PDF preview.");
    }
  }, [data?.endorsement_input]);

  const addComment = useMutation({
    mutationFn: () =>
      api.addShareComment(id, commenterName || "Anonymous", body.trim(), currentPage),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["share", id] });
      setBody("");
    },
  });

  const addReply = useMutation({
    mutationFn: (parentId: number) =>
      api.addShareComment(id, commenterName || "Anonymous", replyBody.trim(), currentPage, parentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["share", id] });
      setReplyBody("");
      setReplyingTo(null);
    },
  });

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAuthed) return null;

  if (!id) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <p className="text-sm text-text-secondary">No share ID provided.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <p className="text-sm text-text-secondary">Loading endorsement…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <p className="text-sm text-error-text">Endorsement not found or link has expired.</p>
      </div>
    );
  }

  const allComments: ShareComment[] = data.comments ?? [];
  const pageComments = allComments.filter((c) => (c.page_number ?? 1) === currentPage);
  const topLevelComments = pageComments.filter((c) => !c.parent_id);
  const repliesMap = pageComments.reduce<Record<number, ShareComment[]>>((acc, c) => {
    if (c.parent_id) (acc[c.parent_id] ??= []).push(c);
    return acc;
  }, {});

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="flex shrink-0 items-center justify-between border-b border-surface-secondary bg-white px-5 py-3">
        <div>
          <h1 className="text-base font-semibold text-near-black">
            {candidateName ? `${candidateName} Endorsement Form` : "Endorsement Form"}
          </h1>
          <p className="text-xs text-text-secondary">Review and leave your comments below.</p>
        </div>
        <button
          onClick={copyLink}
          className="rounded-btn border border-border-card px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
        >
          {copied ? "Link copied!" : "Copy link"}
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <main className="flex flex-1 flex-col overflow-hidden bg-surface-secondary">
          {pdfError ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-error-text">{pdfError}</p>
            </div>
          ) : pdfBlob ? (
            <PdfViewer blob={pdfBlob} currentPage={currentPage} onPageChange={setCurrentPage} />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-text-secondary">Generating PDF…</p>
            </div>
          )}
        </main>

        <aside className="flex w-80 shrink-0 flex-col border-l border-surface-secondary bg-white">
          <div className="border-b border-surface-secondary px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              Page {currentPage} Comments ({pageComments.length})
            </p>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
            {topLevelComments.length === 0 ? (
              <p className="text-xs text-text-secondary">No comments on this page yet.</p>
            ) : (
              topLevelComments.map((c) => (
                <div key={c.id} className="flex gap-2.5">
                  <Avatar name={c.commenter_name} />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-semibold text-near-black">{c.commenter_name}</span>
                    <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-near-black">{c.body}</p>
                    <div className="mt-1 flex items-center gap-3">
                      <span className="text-[10px] text-text-secondary">{formatTime(c.created_at)}</span>
                      <button
                        onClick={() => { setReplyingTo(c.id); setReplyBody(""); }}
                        className="text-[10px] font-semibold text-text-secondary transition-colors hover:text-near-black"
                      >
                        Reply
                      </button>
                    </div>

                    {(repliesMap[c.id] ?? []).length > 0 && (
                      <div className="mt-3 space-y-3 border-l-2 border-surface-secondary pl-3">
                        {(repliesMap[c.id] ?? []).map((r) => (
                          <div key={r.id} className="flex gap-2">
                            <Avatar name={r.commenter_name} sm />
                            <div className="min-w-0 flex-1">
                              <span className="text-xs font-semibold text-near-black">{r.commenter_name}</span>
                              <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-near-black">{r.body}</p>
                              <span className="mt-1 block text-[10px] text-text-secondary">{formatTime(r.created_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {replyingTo === c.id && (
                      <div className="mt-3 flex gap-2">
                        {commenterName && <Avatar name={commenterName} sm />}
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <textarea
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            placeholder={`Reply to ${c.commenter_name}…`}
                            rows={2}
                            autoFocus
                            className="w-full resize-none rounded-2xl border border-border-card bg-surface-secondary px-3 py-2 text-xs text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => addReply.mutate(c.id)}
                              disabled={!replyBody.trim() || addReply.isPending}
                              className="rounded-btn bg-near-black px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-rausch disabled:opacity-50"
                            >
                              {addReply.isPending ? "Posting…" : "Reply"}
                            </button>
                            <button
                              onClick={() => setReplyingTo(null)}
                              className="rounded-btn px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:text-near-black"
                            >
                              Cancel
                            </button>
                          </div>
                          {addReply.isError && (
                            <p className="text-[10px] text-error-text">Failed to post. Please try again.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="shrink-0 border-t border-surface-secondary px-4 py-3">
            <div className="flex gap-2.5">
              {commenterName && <Avatar name={commenterName} />}
              <div className="min-w-0 flex-1 space-y-2">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Leave a comment…"
                  rows={3}
                  className="w-full resize-none rounded-2xl border border-border-card bg-surface-secondary px-3 py-2 text-xs text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
                />
                <button
                  onClick={() => addComment.mutate()}
                  disabled={!body.trim() || addComment.isPending}
                  className="w-full rounded-btn bg-near-black px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-rausch disabled:opacity-50"
                >
                  {addComment.isPending ? "Posting…" : "Post comment"}
                </button>
                {addComment.isError && (
                  <p className="text-[10px] text-error-text">Failed to post. Please try again.</p>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PdfViewer({
  blob,
  currentPage,
  onPageChange,
}: {
  blob: Blob;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTaskRef = useRef<any>(null);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const url = URL.createObjectURL(blob);
    pdfjsLib.getDocument(url).promise.then((doc) => {
      URL.revokeObjectURL(url);
      if (cancelled) { doc.destroy(); return; }
      pdfRef.current?.destroy();
      pdfRef.current = doc;
      setTotalPages(doc.numPages);
      onPageChange(1);
    });
    return () => { cancelled = true; };
  // onPageChange intentionally omitted — only re-run when blob changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!pdf || !canvas || !container || totalPages === 0) return;

    let cancelled = false;
    renderTaskRef.current?.cancel();

    pdf.getPage(currentPage).then((page: pdfjsLib.PDFPageProxy) => {
      if (cancelled) return;
      const dpr = window.devicePixelRatio || 1;
      const containerWidth = container.clientWidth - 32;
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = (containerWidth / baseViewport.width) * dpr;
      const viewport = page.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;

      const ctx = canvas.getContext("2d")!;
      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      task.promise.catch(() => {});
    });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [currentPage, totalPages]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4">
        <canvas ref={canvasRef} className="mx-auto block rounded-card shadow-card" />
      </div>
      {totalPages > 1 && (
        <div className="shrink-0 flex items-center justify-center gap-4 border-t border-surface-secondary bg-white px-4 py-2">
          <button
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            className="rounded-btn px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black disabled:opacity-30"
          >
            ← Prev
          </button>
          <select
            value={currentPage}
            onChange={(e) => onPageChange(Number(e.target.value))}
            className="rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}>
                Page {p}
              </option>
            ))}
          </select>
          <span className="text-sm text-text-secondary">of {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className="rounded-btn px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
